import type { LicenseWithCode } from '@sellokart/shared';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { CopyButton } from '@/components/CopyButton';
import { Modal } from '@/components/Modal';
import { formatDateTime } from '@/lib/format';

export interface LicenseCodeModalProps {
  issued: LicenseWithCode | null;
  onClose: () => void;
}

/**
 * The plaintext activation code exists only in this dialog — the server stores a
 * SHA-256 hash, so it can never be shown again. If it is lost before the
 * customer activates, the licence has to be revoked and re-issued.
 *
 * Deliberately near-identical to DeviceTokenModal: the discipline is the same,
 * and an operator who has used one should recognise the other immediately.
 */
export function LicenseCodeModal({ issued, onClose }: LicenseCodeModalProps) {
  return (
    <Modal
      open={issued !== null}
      title="Activation code created"
      description={issued ? `Till “${issued.label}”` : undefined}
      size="lg"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose} data-autofocus>
          I have saved the code
        </Button>
      }
    >
      {issued ? (
        <div className="space-y-5">
          <Alert tone="warning" title="Shown once — copy it now">
            This code is stored hashed and cannot be displayed again. If it is lost
            before the till is activated, revoke this licence and issue a new one.
          </Alert>

          <div>
            <p className="label">Activation code</p>
            <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50/60 p-3 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 break-all font-mono text-lg tracking-wide text-slate-900">
                {issued.code}
              </code>
              <CopyButton
                value={issued.code}
                label="Copy code"
                className="shrink-0 self-start sm:self-auto"
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-surface/70 p-4 text-sm text-slate-700">
            <p className="text-sm font-semibold text-slate-900">What happens next</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                The till needs internet <strong>once</strong>, for a few seconds, to
                redeem this. After that it runs offline indefinitely.
              </li>
              <li>
                The licence term starts when the code is redeemed, not now — an
                unused code does not eat into it.
              </li>
              {issued.redeemBy ? (
                <li>
                  Must be redeemed before{' '}
                  <strong>{formatDateTime(issued.redeemBy)}</strong>, after which it
                  stops working and you issue a new one.
                </li>
              ) : null}
              <li>
                Redeeming also pairs the till for cloud sync — there is no second
                token to hand over.
              </li>
            </ul>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
